/**
 * Custom hook for file handling in Data Adjustments
 * 
 * Responsibilities:
 * - File selection with validation (format, size, duplicates)
 * - CSV validation (validateCSVFile)
 * - Time range extraction and overlap validation
 * - Multi-file chunked upload with progress tracking
 * - Upload ID management
 */

import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import AdjustmentsOfDataChunkedUploader from '../services/AdjustmentsOfDataChunkedUploader';
import { validateCSVFiles } from '../utils/csvValidators';
import { validateTimeOverlap, calculateOverlappingRange } from '../utils/timeRangeHelpers';
import { formatDateToInput } from '../../../utils/dateFormatters';
import type { UploadProgress as StandardUploadProgress } from '@/shared/components/feedback/ProgressTracker';
import type { AdjustmentsUploadProgress } from '../../../types/common';
import type {
  InfoType,
  DateTimeState,
} from '../types/dataAdjustments.types';
import { useFileSizeLimit } from '@/hooks/useFileSizeLimit';

interface ProcessedData {
  success: boolean;
  methodsRequired?: boolean;
  data: {
    info_df: Array<InfoType>;
    upload_id: string;
  };
}

interface UseAdjustmentsFileProps {
  onStartTimeChange: (state: DateTimeState) => void;
  onEndTimeChange: (state: DateTimeState) => void;
  onShowUpgradeModal?: (message: string, type: 'upload' | 'processing' | 'storage') => void;
}

interface UseAdjustmentsFileReturn {
  // State
  selectedFiles: File[];
  uploadIds: { [key: string]: string };
  processedData: ProcessedData | null;
  
  // Progress
  standardProgress: StandardUploadProgress | null;
  isUploading: boolean;
  
  // Error/Success
  /** Structured or plain-string error — page dispatches directly to uiReducer (D5/CC-3) */
  error: string | { message: string; code?: string; suggestion?: string } | null;
  successMessage: string | null;
  
  // Refs
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  
  // Handlers
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveFile: (index: number) => void;
  handleClearAllFiles: () => void;
  handleUpload: () => Promise<void>;
  
  // Setters
  setError: (error: string | { message: string; code?: string; suggestion?: string } | null) => void;
  setSuccessMessage: (message: string | null) => void;
  
  // Reset
  resetFiles: () => void;
}

export const useAdjustmentsFile = ({
  onStartTimeChange,
  onEndTimeChange,
  onShowUpgradeModal,
}: UseAdjustmentsFileProps): UseAdjustmentsFileReturn => {
  const { t } = useTranslation('dataAdjustments');
  const { maxFileSizeBytes, formattedMaxSize } = useFileSizeLimit();
  
  // File state
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadIds, setUploadIds] = useState<{ [key: string]: string }>({});
  const [processedData, setProcessedData] = useState<ProcessedData | null>(null);
  
  // Progress state
  const [standardProgress, setStandardProgress] = useState<StandardUploadProgress | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Message state
  const [error, setError] = useState<string | { message: string; code?: string; suggestion?: string } | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const processingCallbacksRef = useRef<Map<number, (progress: AdjustmentsUploadProgress) => void>>(new Map());
  const currentProcessingIdRef = useRef(0);
  const fileProgressMapRef = useRef<Map<string, number>>(new Map());
  const fileSizeMapRef = useRef<Map<string, number>>(new Map());

  /**
   * Calculate overall upload progress using bytes-weighted average
   */
  const calculateOverallUploadProgress = useCallback((): number => {
    const progressMap = fileProgressMapRef.current;
    const sizeMap = fileSizeMapRef.current;

    if (progressMap.size === 0) return 0;

    let totalBytes = 0;
    let uploadedBytes = 0;

    progressMap.forEach((progress, fileName) => {
      const fileSize = sizeMap.get(fileName) || 0;
      totalBytes += fileSize;
      uploadedBytes += fileSize * (progress / 100);
    });

    if (totalBytes === 0) return 0;
    return Math.min(100, (uploadedBytes / totalBytes) * 100);
  }, []);

  /**
   * Process single file upload
   */
  const processFileContent = useCallback(async (
    file: File, 
    sharedUploadId: string
  ): Promise<ProcessedData> => {
    currentProcessingIdRef.current += 1;
    const currentUploadId = currentProcessingIdRef.current;

    const uploadCallback = (progress: AdjustmentsUploadProgress) => {
      if (processingCallbacksRef.current.has(currentUploadId)) {
        const fileUploadProgress = progress.uploadProgress ?? 0;
        fileProgressMapRef.current.set(file.name, fileUploadProgress);

        const overallUploadProgress = calculateOverallUploadProgress();

        setStandardProgress({
          fileName: file.name,
          uploadProgress: overallUploadProgress,
          processingProgress: progress.processingProgress ?? 0,
          status: progress.status,
          error: progress.error || undefined,
          message: progress.detail || progress.message,
          step: progress.step,
          eta: progress.eta,
          etaFormatted: progress.etaFormatted,
          currentFile: progress.currentFile,
          totalFiles: progress.totalFiles,
        });
      }
    };

    processingCallbacksRef.current.set(currentUploadId, uploadCallback);

    const result = await AdjustmentsOfDataChunkedUploader.uploadFile(
      file,
      uploadCallback,
      sharedUploadId
    );

    processingCallbacksRef.current.delete(currentUploadId);

    const uploadId = result.data?.upload_id || sharedUploadId;
    if (uploadId) {
      setUploadIds((prev) => ({
        ...prev,
        [file.name]: uploadId,
      }));
    } else {
      throw new Error('Upload failed: No upload_id received');
    }

    if (result.error) {
      throw new Error(result.error);
    }

    if (!result.success || !result.data) {
      throw new Error(t('errors.invalidResponseFormat'));
    }

    return result as ProcessedData;
  }, [t, calculateOverallUploadProgress]);

  /**
   * Handle file selection (without uploading)
   */
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newFiles = Array.from(files);

    // Validate file format
    const invalidFiles = newFiles.filter(
      (file) => !file.name.toLowerCase().endsWith('.csv')
    );

    if (invalidFiles.length > 0) {
      setError(t('errors.invalidFormat'));
      return;
    }

    // Add new files, avoiding duplicates
    setSelectedFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.name));
      const uniqueNewFiles = newFiles.filter((f) => !existingNames.has(f.name));

      if (uniqueNewFiles.length === 0) {
        setError(t('errors.duplicateFiles'));
        return prev;
      }

      // Validate total file size (dynamic limit based on subscription plan)
      const currentTotalSize = prev.reduce((acc, f) => acc + f.size, 0);
      const newFilesSize = uniqueNewFiles.reduce((acc, f) => acc + f.size, 0);
      const projectedTotalSize = currentTotalSize + newFilesSize;

      if (projectedTotalSize > maxFileSizeBytes) {
        const currentMB = (currentTotalSize / (1024 * 1024)).toFixed(2);
        const newMB = (newFilesSize / (1024 * 1024)).toFixed(2);
        const message = t('errors.fileSizeExceeded', {
          current: currentMB,
          new: newMB,
          maxSize: formattedMaxSize,
        });
        if (onShowUpgradeModal) {
          onShowUpgradeModal(message, 'storage');
        } else {
          setError(message);
        }
        return prev;
      }

      setError(null);
      return [...prev, ...uniqueNewFiles];
    });

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [t, maxFileSizeBytes, formattedMaxSize, onShowUpgradeModal]);

  /**
   * Remove individual file from selection
   */
  const handleRemoveFile = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  /**
   * Clear all selected files
   */
  const handleClearAllFiles = useCallback(() => {
    setSelectedFiles([]);
    setError(null);
  }, []);

  /**
   * Handle file upload and processing
   */
  const handleUpload = useCallback(async () => {
    if (isUploading) return;
    if (selectedFiles.length === 0) {
      setError(t('errors.noFileSelected'));
      return;
    }

    setIsUploading(true);
    setError(null);
    setProcessedData(null);
    setSuccessMessage(null);

    // Reset progress tracking
    fileProgressMapRef.current.clear();
    fileSizeMapRef.current.clear();
    selectedFiles.forEach((file) => {
      fileProgressMapRef.current.set(file.name, 0);
      fileSizeMapRef.current.set(file.name, file.size);
    });

    try {
      // Validate all CSV files
      const validationError = await validateCSVFiles(selectedFiles, t);
      if (validationError) {
        setError(`${validationError.file.name}: ${validationError.error}`);
        setIsUploading(false);
        return;
      }

      // Pre-upload validation: Check for time overlap
      if (selectedFiles.length >= 2) {
        const overlapValidation = await validateTimeOverlap(selectedFiles, t);
        if (!overlapValidation.isValid) {
          setError(overlapValidation.error || t('errors.noOverlappingTimeIntervals'));
          setIsUploading(false);
          return;
        }
      }

      // Generate shared upload ID for all files
      const sharedUploadId = `adj_${crypto.randomUUID()}`;

      // Capture the first enriched error so it can reach the mapper-aware outer catch
      let firstError: (Error & { code?: string; suggestion?: string }) | null = null;

      // Upload all files
      const results = await Promise.all(
        selectedFiles.map(async (file) => {
          try {
            return await processFileContent(file, sharedUploadId);
          } catch (err) {
            if (!firstError && err instanceof Error) {
              firstError = err as Error & { code?: string; suggestion?: string };
            }
            return null;
          }
        })
      );

      // Filter successful results
      const successfulResults = results.filter(
        (result): result is ProcessedData => result !== null
      );

      if (successfulResults.length === 0) {
        // Re-throw the first captured error so the outer catch + mapper can translate it;
        // fall back to a generic error only when no enriched error was captured.
        if (firstError) {
          throw firstError;
        }
        setError(t('errors.noFilesProcessed'));
        return;
      }

      // Combine results
      const fileUploadIds: { [key: string]: string } = {};
      const allInfoDf: InfoType[] = [];

      successfulResults.forEach((result) => {
        if (result?.data?.info_df && result.data.upload_id) {
          result.data.info_df.forEach((info) => {
            const fileName = info['Name der Datei'];
            fileUploadIds[fileName] = result.data.upload_id;
            allInfoDf.push(info);
          });
        }
      });

      // Stabilise file ordering: sort allInfoDf to match the user-selected file order.
      // The backend may return info_df entries with a '_processed' suffix; strip it for
      // the comparison so Q_1_processed.csv maps back to Q_1.csv.
      const selectedFileNames = selectedFiles.map((f) => f.name);
      allInfoDf.sort((a, b) => {
        const nameA = a['Name der Datei'].replace('_processed', '');
        const nameB = b['Name der Datei'].replace('_processed', '');
        const idxA = selectedFileNames.findIndex((n) => nameA.includes(n.replace('.csv', '')) || n.includes(nameA.replace('.csv', '')));
        const idxB = selectedFileNames.findIndex((n) => nameB.includes(n.replace('.csv', '')) || n.includes(nameB.replace('.csv', '')));
        return (idxA === -1 ? Infinity : idxA) - (idxB === -1 ? Infinity : idxB);
      });

      // Calculate overlapping time range
      const startEndTimes = allInfoDf.map((info) => ({
        start: new Date(info['Startzeit (UTC)']),
        end: new Date(info['Endzeit (UTC)']),
      }));

      if (startEndTimes.length > 0) {
        const overlap = calculateOverlappingRange(startEndTimes);
        
        if (overlap) {
          onStartTimeChange({
            value: formatDateToInput(overlap.start),
            timestamp: overlap.start.getTime(),
          });
          onEndTimeChange({
            value: formatDateToInput(overlap.end),
            timestamp: overlap.end.getTime(),
          });
        } else {
          setError(t('errors.noOverlappingTimeIntervals'));
          return;
        }
      }

      const combinedData: ProcessedData = {
        success: true,
        data: {
          info_df: allInfoDf,
          upload_id: Object.values(fileUploadIds)[0] || '',
        },
      };

      setProcessedData(combinedData);
      setUploadIds(fileUploadIds);
      setSuccessMessage(t('success.allProcessed'));

    } catch (err) {
      // D5/CC-3: pass structured payload to the page; render-time mapper in
      // DataAdjustmentsPage re-translates on language switch instead of
      // baking the current locale into the stored string here.
      const e = err as Error & { code?: string; suggestion?: string };
      const fallback = e?.message ?? t('errors.unknownError', { defaultValue: 'An unknown error occurred' });
      setError({
        message: fallback,
        code: e?.code,
        suggestion: e?.suggestion,
      });
    } finally {
      setIsUploading(false);
      setStandardProgress(null);
    }
  }, [isUploading, selectedFiles, t, processFileContent, onStartTimeChange, onEndTimeChange]);

  /**
   * Reset all file state
   */
  const resetFiles = useCallback(() => {
    // Disconnect socket connections
    const uploadIdsList = Object.values(uploadIds);
    if (uploadIdsList.length > 0) {
      uploadIdsList.forEach((uploadId) => {
        AdjustmentsOfDataChunkedUploader.disconnectSocket(uploadId);
      });
    } else {
      AdjustmentsOfDataChunkedUploader.disconnectSocket();
    }

    setSelectedFiles([]);
    setUploadIds({});
    setProcessedData(null);
    setStandardProgress(null);
    setIsUploading(false);
    setError(null);
    setSuccessMessage(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    fileProgressMapRef.current.clear();
    fileSizeMapRef.current.clear();
  }, [uploadIds]);

  return {
    // State
    selectedFiles,
    uploadIds,
    processedData,
    
    // Progress
    standardProgress,
    isUploading,
    
    // Error/Success
    error,
    successMessage,
    
    // Refs
    fileInputRef,
    
    // Handlers
    handleFileSelect,
    handleRemoveFile,
    handleClearAllFiles,
    handleUpload,
    
    // Setters
    setError,
    setSuccessMessage,
    
    // Reset
    resetFiles,
  };
};

export default useAdjustmentsFile;
